"use client";

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { OFFLINE_ACCOUNT_EVENT, offlineContext } from "@/lib/offline/context";
import type { LibraryContext } from "@/native-shell/library";

const RecordingAccountContext = createContext<LibraryContext | null>(null);
export function useRecordingAccountContext() { return useContext(RecordingAccountContext); }

/** Unmount private views on account changes, including their pending readers. */
export function RecordingAccountBoundary({ children, quiet = false, expectedOwner, readOnly = false }: {
  children: ReactNode; quiet?: boolean; expectedOwner?: string; readOnly?: boolean;
}) {
  const [status, setStatus] = useState<"checking" | "ready" | "changed" | "unavailable">(readOnly ? "ready" : "checking");
  const [context, setContext] = useState<LibraryContext | null>(null);
  useEffect(() => {
    if (readOnly) return;
    let active = true, initialising = true;
    const reset = () => { if (!initialising) setStatus("changed"); };
    window.addEventListener(OFFLINE_ACCOUNT_EVENT, reset);
    void offlineContext().then(context => {
      initialising = false;
      if (active) { setContext(context); setStatus(expectedOwner && context.owner !== expectedOwner ? "changed" : "ready"); }
    }, () => { initialising = false; if (active) setStatus("unavailable"); });
    return () => { active = false; window.removeEventListener(OFFLINE_ACCOUNT_EVENT, reset); };
  }, [expectedOwner, readOnly]);
  if (status === "ready") return <RecordingAccountContext.Provider value={context}>{children}</RecordingAccountContext.Provider>;
  if (quiet) return null;
  if (status === "checking") return <p className="p-5 text-sm text-text-muted" role="status">Opening your private sessions…</p>;
  return <section className="mx-auto max-w-3xl rounded-2xl border border-border bg-white p-5" role="status">
    <h2 className="font-heading text-lg font-bold text-navy">{status === "changed" ? "Your account changed" : "Your sessions could not be opened"}</h2>
    <p className="mt-2 text-sm text-text-muted">Reopen this page to check the sessions available to your current account.</p>
    <button type="button" className="mt-3 min-h-11 rounded-lg bg-navy px-4 text-sm font-semibold text-white" onClick={() => window.location.reload()}>Reopen this page</button>
  </section>;
}
